/**
 * Client "Monitoring" — the team's monitored subjects. Where the digest
 * email links to.
 *
 * Read-only. Each row counts only the findings the team has actually been
 * sent; the server decides that, and nothing an analyst has not released
 * appears here.
 */

import { useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  Badge,
  Button,
  Card,
  Center,
  Container,
  Group,
  Loader,
  Pagination,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';

import { useSearchDraft } from '@/hooks';
import { useClientMonitoredSubjects } from '@/hooks/useClientMonitoring';
import {
  CLIENT_SUBJECT_STATUS_COLOURS,
  CLIENT_SUBJECT_STATUS_LABELS,
  formatMonitoringDate as formatDate,
  MonitoredSubjectStatus,
  monitoringComponentLabel,
  TeamSubjectRow,
} from '@/types/monitoring';

const PAGE_SIZE = 25;

const STATUS_OPTIONS = (
  Object.entries(CLIENT_SUBJECT_STATUS_LABELS) as [MonitoredSubjectStatus, string][]
).map(([value, label]) => ({ value, label }));

function isStatus(value: string | null): value is MonitoredSubjectStatus {
  return value !== null && value in CLIENT_SUBJECT_STATUS_LABELS;
}

export function ClientMonitoringPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Number.parseInt(searchParams.get('page') ?? '1', 10);
  const q = searchParams.get('q') ?? '';
  const statusParam = searchParams.get('status');
  const status = isStatus(statusParam) ? statusParam : undefined;

  const updateParams = useCallback(
    (updates: Record<string, string | number>, opts?: { replace?: boolean }) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(updates)) {
            if (value === '' || value === 0) {
              next.delete(key);
            } else {
              next.set(key, String(value));
            }
          }
          return next;
        },
        opts?.replace ? { replace: true } : undefined
      );
    },
    [setSearchParams]
  );

  const [searchValue, setSearchValue] = useSearchDraft(q, (settled) =>
    updateParams({ q: settled, page: 1 }, { replace: true })
  );

  const {
    page: result,
    isLoading,
    error,
  } = useClientMonitoredSubjects({
    page,
    pageSize: PAGE_SIZE,
    q: q || undefined,
    status,
  });
  const totalPages = Math.ceil(result.total / PAGE_SIZE);
  const hasFilters = Boolean(q || status);

  return (
    <Container size="xl" py="md">
      <Stack gap="md">
        <Stack gap={4}>
          <Title order={2}>Monitoring</Title>
          <Text size="sm" c="dimmed">
            Subjects we are watching for you. Open one to see the findings we have sent about it.
          </Text>
        </Stack>

        <Group gap="xs" wrap="nowrap">
          <TextInput
            placeholder="Search subjects…"
            leftSection={<IconSearch size={16} />}
            value={searchValue}
            onChange={(e) => setSearchValue(e.currentTarget.value)}
            style={{ flex: 1, minWidth: 180 }}
            data-testid="client-monitoring-search"
          />
          <Select
            placeholder="Status"
            data={STATUS_OPTIONS}
            value={status ?? null}
            onChange={(value) => updateParams({ status: value ?? '', page: 1 })}
            clearable
            w={150}
            style={{ flexShrink: 0 }}
          />
          {hasFilters && (
            <Button
              variant="subtle"
              size="compact-sm"
              c="dimmed"
              style={{ flexShrink: 0 }}
              onClick={() => {
                setSearchValue('');
                updateParams({ q: '', status: '', page: 1 });
              }}
            >
              Clear
            </Button>
          )}
        </Group>

        <Card shadow="sm" padding="lg" radius="md">
          <SubjectsContent
            items={result.items}
            isLoading={isLoading}
            error={error}
            hasFilters={hasFilters}
          />
        </Card>

        {totalPages > 1 && (
          <Group justify="flex-end">
            <Pagination
              value={page}
              onChange={(p) => updateParams({ page: p })}
              total={totalPages}
              size="sm"
            />
          </Group>
        )}
      </Stack>
    </Container>
  );
}

function SubjectsContent({
  items,
  isLoading,
  error,
  hasFilters,
}: Readonly<{
  items: TeamSubjectRow[];
  isLoading: boolean;
  error: Error | null;
  hasFilters: boolean;
}>) {
  const navigate = useNavigate();

  if (isLoading && items.length === 0) {
    return (
      <Center h={300}>
        <Loader />
      </Center>
    );
  }

  if (error) {
    return (
      <Center h={300}>
        <Text c="red">{error.message}</Text>
      </Center>
    );
  }

  if (items.length === 0) {
    return (
      <Center h={300}>
        <Text c="dimmed">
          {hasFilters ? 'No subjects match your filters.' : 'No subjects are being monitored yet.'}
        </Text>
      </Center>
    );
  }

  return (
    <Table striped highlightOnHover>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Subject</Table.Th>
          <Table.Th>Checks</Table.Th>
          <Table.Th>Status</Table.Th>
          <Table.Th>Findings sent</Table.Th>
          <Table.Th>Last sent</Table.Th>
          <Table.Th>Monitored since</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {items.map((row) => (
          <Table.Tr
            key={row.id}
            onClick={() => navigate(`/monitoring/subjects/${row.id}`)}
            style={{ cursor: 'pointer' }}
            data-testid={`client-monitored-subject-${row.id}`}
          >
            <Table.Td>
              <Text size="sm" fw={500}>
                {row.name}
              </Text>
            </Table.Td>
            <Table.Td>
              <Group gap={4}>
                {row.scopeComponentSlugs.map((slug) => (
                  <Badge key={slug} size="sm" variant="outline" color="gray">
                    {monitoringComponentLabel(slug)}
                  </Badge>
                ))}
              </Group>
            </Table.Td>
            <Table.Td>
              <Badge color={CLIENT_SUBJECT_STATUS_COLOURS[row.status]} variant="light">
                {CLIENT_SUBJECT_STATUS_LABELS[row.status]}
              </Badge>
            </Table.Td>
            <Table.Td>
              <Text size="sm">{row.sentAlertCount}</Text>
            </Table.Td>
            <Table.Td>
              <Text size="sm">{formatDate(row.lastSentAt)}</Text>
            </Table.Td>
            <Table.Td>
              <Text size="sm">{formatDate(row.enrolledAt)}</Text>
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
}
